import { useState } from 'react';
import { toTitleCase } from 'utils/shared/toTitleCase';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { useModal } from 'hooks/useModal';
import Calendar from 'svg/Calendar';
import CloseIcon from 'svg/CloseIcon';
import LiveIcon from 'svg/LiveIcon';
import Button, { ButtonText } from 'components/Button';
import { formatCreationWithIntl } from 'components/Premium/utils';
import RecapDetailModal from './RecapDetailModal';
import { LatestRecapCardProps, LatestRecapType } from '../props';

const LatestRecapCard: React.FC<LatestRecapCardProps> = ({
  recap,
  handleBookAnotherLesson,
  onDismiss,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const recapDetailModal = useModal();

  const handleClose = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  const handleSeeDetails = () => {
    onDismiss?.();
    recapDetailModal.openModal();
  };

  const handleDetailModalClose = () => {
    recapDetailModal.closeModal();
    // Once the player has viewed the recap, hide the reminder for this view.
    if (onDismiss) setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      <div
        className="absolute right-0 top-0 z-10 flex w-full max-w-[25rem] flex-col gap-ds-lg rounded-3xl bg-color-brand-primary p-ds-xl 
        text-white shadow-[0px_8px_24px_0px_rgba(0,0,0,0.25)] max-sm:static max-sm:mb-ds-xl max-sm:gap-ds-md 
        max-sm:p-ds-lg"
      >
        <div className="flex justify-between">
          <div className="flex items-center gap-2">
            <LiveIcon className="h-[18px] w-[10px]" />
            <span className="max-sm:typography-product-subheading typography-product-heading-compact-desktop">
              New Recap
            </span>
          </div>
          <ButtonText onClick={handleClose}>
            <CloseIcon className="h-5 w-5" />
          </ButtonText>
        </div>
        <div className="flex gap-ds-md">
          <img
            src={getProfileImageUrlOrPlaceholder({
              path: recap.lessonCoach?.profileImagePath || '',
            })}
            className="h-12 w-12 rounded-full bg-white object-cover object-top"
            alt="coach-avatar"
          />
          <div className="flex flex-col gap-1">
            <span className="max-sm:typography-product-card-title-mobile typography-product-card-title-desktop">
              {toTitleCase(recap.lessonCoach?.fullName || '')}
            </span>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 max-sm:h-3 max-sm:w-3" />
              <span className="typography-product-text-card-desktop max-sm:typography-product-text-card-mobile text-brand-gray-25">
                {formatCreationWithIntl(recap.lesson?.times?.[0]?.startDateTime)}
              </span>
            </div>
          </div>
        </div>
        <Button
          variant="primary-light"
          size="md"
          className="h-[2.6rem]"
          onClick={handleSeeDetails}
        >
          See Details
        </Button>
      </div>
      <RecapDetailModal
        isOpen={recapDetailModal.isOpen}
        handleClose={handleDetailModalClose}
        recap={recap}
        handleBookAnotherLesson={(coachId: string) => {
          recapDetailModal.closeModal();
          handleBookAnotherLesson(coachId);
        }}
      />
    </>
  );
};

export default LatestRecapCard;
